/**
 * Toasts (Radix): call `toast({ title, description, variant })` from anywhere; `<Toaster />` is
 * mounted once in app.tsx.
 */
import * as ToastPrimitive from '@radix-ui/react-toast';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { forwardRef, useSyncExternalStore } from 'react';
import type { ComponentPropsWithoutRef, ComponentRef, ReactNode } from 'react';
import { cn } from '@/lib/utils';

const toastVariants = cva(
  'pointer-events-auto relative flex w-full items-start gap-3 rounded-lg border p-4 pr-12 shadow-lg data-[state=closed]:opacity-0',
  {
    variants: {
      variant: {
        default: 'border-border bg-background text-foreground',
        success: 'border-success/40 bg-success-soft text-foreground',
        error: 'border-danger/40 bg-danger-soft text-foreground',
        warning: 'border-warning/40 bg-warning-soft text-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export type ToastVariant = NonNullable<VariantProps<typeof toastVariants>['variant']>;

export interface ToastInput {
  title: ReactNode;
  description?: ReactNode;
  variant?: ToastVariant;
  /** Milliseconds; default 5000 (`Infinity` = until closed). */
  duration?: number;
  /** Button inside the toast (e.g. "Aggiorna"); the toast closes after the click. */
  action?: { label: string; onClick: () => void };
}

interface ToastItem extends ToastInput {
  id: number;
  open: boolean;
}

// ---------------------------------------------------------------- store

let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function toast(input: ToastInput): number {
  const id = nextId++;
  items = [...items, { ...input, id, open: true }].slice(-4);
  emit();
  return id;
}

export function dismissToast(id: number): void {
  items = items.map((t) => (t.id === id ? { ...t, open: false } : t));
  emit();
  setTimeout(() => {
    items = items.filter((t) => t.id !== id);
    emit();
  }, 300);
}

function useToasts(): ToastItem[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => items,
    () => items,
  );
}

// ---------------------------------------------------------------- primitives

export const ToastProvider = ToastPrimitive.Provider;

export const ToastViewport = forwardRef<
  ComponentRef<typeof ToastPrimitive.Viewport>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    data-print="hide"
    className={cn(
      'fixed inset-x-0 top-0 z-[100] flex max-h-dvh flex-col gap-2 p-4 sm:bottom-0 sm:left-auto sm:right-0 sm:top-auto sm:w-[26rem]',
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = 'ToastViewport';

export const Toast = forwardRef<
  ComponentRef<typeof ToastPrimitive.Root>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => (
  <ToastPrimitive.Root ref={ref} className={cn(toastVariants({ variant }), className)} {...props} />
));
Toast.displayName = 'Toast';

export const ToastTitle = forwardRef<
  ComponentRef<typeof ToastPrimitive.Title>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title ref={ref} className={cn('text-sm font-semibold', className)} {...props} />
));
ToastTitle.displayName = 'ToastTitle';

export const ToastDescription = forwardRef<
  ComponentRef<typeof ToastPrimitive.Description>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
ToastDescription.displayName = 'ToastDescription';

export const ToastClose = forwardRef<
  ComponentRef<typeof ToastPrimitive.Close>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Close
    ref={ref}
    aria-label="Chiudi"
    className={cn(
      'absolute right-1 top-1 inline-flex size-11 items-center justify-center rounded-md text-muted-foreground hover:bg-accent',
      className,
    )}
    {...props}
  >
    <X className="size-4" />
  </ToastPrimitive.Close>
));
ToastClose.displayName = 'ToastClose';

export function Toaster() {
  const toasts = useToasts();
  return (
    <ToastProvider swipeDirection="right" label="Notifiche">
      {toasts.map((t) => (
        <Toast
          key={t.id}
          variant={t.variant}
          open={t.open}
          duration={t.duration ?? 5000}
          onOpenChange={(open) => {
            if (!open) dismissToast(t.id);
          }}
        >
          <div className="grid flex-1 gap-1">
            <ToastTitle>{t.title}</ToastTitle>
            {t.description ? <ToastDescription>{t.description}</ToastDescription> : null}
          </div>
          {t.action ? (
            <ToastPrimitive.Action
              altText={t.action.label}
              onClick={t.action.onClick}
              className="inline-flex h-11 shrink-0 items-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary-hover"
            >
              {t.action.label}
            </ToastPrimitive.Action>
          ) : null}
          <ToastClose />
        </Toast>
      ))}
      <ToastViewport />
    </ToastProvider>
  );
}
